import { TitleCasePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, resource, signal, ViewEncapsulation } from "@angular/core";
import { MatIconButton } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { TranslocoModule } from "@ngneat/transloco";
import { parse, startOfToday } from "date-fns";
import { firstValueFrom } from "rxjs";
import { DialogsService } from "../../dialogs.service";
import { LocalizeDatePipe } from "../../../core/pipes/localizeDate.pipe";
import { PremisesLink } from "../../../modules/admin/projects/premises-management/premises.types";
import { PremisesServices } from "../../../modules/admin/projects/premises-management/premises.services";

@Component({
    selector: 'hotel-month-concepts-dialog',
    templateUrl: './hotel-month-concepts-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
        .month-concepts-dialog-panel {

            .mat-mdc-dialog-container {
        
                .mat-mdc-dialog-surface {
                    padding: 0 !important;
                }
            }
        }
        `
    ],
    imports: [LocalizeDatePipe, MatIcon, MatIconButton, MatTooltip, TitleCasePipe, TranslocoModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class HotelMonthConceptsDialogComponent {

    public data: { period: string, year: string, premises?: PremisesLink, calendarToken?: string, premisesCode?: string } = inject(MAT_DIALOG_DATA);
    private dialogRef = inject(MatDialogRef<HotelMonthConceptsDialogComponent>);
    private dialogsService = inject(DialogsService);
    private premisesServices = inject(PremisesServices);

    period = signal(this.data.period);
    year = signal(this.data.year);
    date = computed(() => parse(`${this.year()}-${this.period()}-01`, 'yyyy-MM-dd', startOfToday()));
    premises = signal(this.data.premises);
    calendarToken = signal(this.data.calendarToken);
    premisesCode = signal(this.data.premisesCode);

    totalByNet4Concepts = resource({
        params: () => ({ period: this.period(), year: this.year(), premises: this.premises(), calendarToken: this.calendarToken() }),
        loader: async (request) => {
            const period = request.params.period;
            const year = request.params.year;
            const premises = request.params.premises;
            const calendarToken = request.params.calendarToken;
            if (period && year) {
                try {
                    if (premises) {
                        return await firstValueFrom(this.premisesServices.retrieveMonthlyTotalByNet4Concept({
                            period,
                            year,
                            premisesId: premises.premisesId,
                            serviceId: premises.serviceId,
                            workplaceOid: premises.workplaceOid
                        }));
                    } else if (calendarToken) {
                        return await firstValueFrom(this.premisesServices.retrieveMonthlyTotalByNet4Concept4Client({
                            period,
                            year,
                            calendarToken
                        }));
                    }
                } catch (error) {
                    this.dialogsService.openErrorDialog();
                }
            }
        }
    });

    closeDialog() {
        this.dialogRef.close();
    }

    async printElement() {
        const element = document.getElementById('printableContent');
        if (!element) return;
        element.classList.add('printable');
        element.firstElementChild?.classList.remove('hidden');

        const WindowPrt = document.createElement('iframe');
        WindowPrt.style.position = 'absolute';
        WindowPrt.style.top = '-10000px';
        WindowPrt.style.width = '0px';
        WindowPrt.style.height = '0px';
        document.body.appendChild(WindowPrt);

        const doc = WindowPrt.contentWindow?.document;
        if (!doc) return;

        const validStyleLinks = await this.getValidStyleLinks();
        const inlineStyles = Array.from(document.querySelectorAll('style'))
            .map(style => style.outerHTML)
            .join('\n');

        doc.open();
        doc.write(`<html><head>${validStyleLinks.join('\n')}${inlineStyles}</head><body></body></html>`);
        doc.close();

        const cloned = element.cloneNode(true) as HTMLElement;
        element.classList.remove('printable');
        element.firstElementChild?.classList.add('hidden');
        setTimeout(() => {
            const body = WindowPrt.contentDocument?.body;
            if (!body) return;

            body.appendChild(cloned);
            WindowPrt.contentWindow?.focus();
            WindowPrt.contentWindow?.print();
            setTimeout(() => {
                document.body.removeChild(WindowPrt);
            }, 1000);
        }, 100);
    }

    minutesToString(minutes: number) {
        let hours = minutes < 0 ? Math.ceil(minutes / 60) : Math.floor(minutes / 60);
        let str = minutes < 0 ? '-' : '';
        minutes = Math.round(minutes % 60);
        if (hours !== 0) {
            str = str + String(Math.abs(hours)) + 'h ';
        }
        if (minutes !== 0) {
            str = str + String(Math.abs(minutes)) + 'm';
        }
        if (str === '') {
            return '0m';
        } else {
            return str.trim();
        }
    }

    private async getValidStyleLinks(): Promise<string[]> {
        const linkElements = Array.from(document.querySelectorAll('link[rel="stylesheet"]')) as HTMLLinkElement[];

        const validLinks: string[] = [];

        for (const link of linkElements) {
            try {
                const response = await fetch(link.href, { method: 'HEAD' });
                const contentType = response.headers.get('Content-Type') || '';
                if (response.ok && contentType.includes('text/css')) {
                    validLinks.push(`<link rel="stylesheet" href="${link.href}">`);
                }
            } catch (_) {
                // ignore broken links
            }
        }

        return validLinks;
    }
}